/**
 * Rachtian Hero Marquee - Infinite Scrolling Animation
 * Implements endless horizontal scrolling marquee with seamless loop
 */
class RachtianHeroMarquee {
    constructor(marqueeElement) {
        this.marquee = marqueeElement;
        this.track = marqueeElement.querySelector("[data-marquee-track]");
        this.contentElements = marqueeElement.querySelectorAll("[data-marquee-content]");

        if (!this.track || this.contentElements.length < 2) {
            return;
        }

        // Check if initial animation is enabled by looking for the class on parent section
        const section = marqueeElement.closest(".marquee-hero--initial-animation");
        this.initialAnimationEnabled = section !== null;

        this.isAnimating = false;
        this.animationId = null;
        this.speed = 50; // pixels per second
        this.currentPosition = 0;
        this.contentWidth = 0;
        this.lastFrameTime = null;
        this.resizeHandler = null;
        this.initialAnimationComplete = false;
        this.resizeObserver = null;
        this.setupComplete = false;

        this.init();
    }

    async init() {
        // Wait for fonts to load before measuring
        if (document.fonts && document.fonts.ready) {
            await document.fonts.ready;
        }
        
        // Use a small delay to ensure DOM is fully rendered
        setTimeout(() => this.setupMarquee(), 0);
    }

    setupMarquee() {
        // Use ResizeObserver to wait for both content divs to be fully laid out
        // This ensures accurate width calculation on mobile where rendering may be delayed
        let observedCount = 0;
        const totalElements = this.contentElements.length;
        let maxWidth = 0;

        const checkAndSetup = () => {
            // Calculate width from all content elements and use the maximum
            let allReady = true;
            let currentMaxWidth = 0;

            this.contentElements.forEach((content) => {
                const width = content.scrollWidth || content.offsetWidth;
                if (width > 0) {
                    currentMaxWidth = Math.max(currentMaxWidth, width);
                } else {
                    allReady = false;
                }
            });

            // If all elements have valid widths, proceed with setup
            if (allReady && currentMaxWidth > 0) {
                this.contentWidth = currentMaxWidth;

                // Set explicit width on both content blocks to ensure they're identical
                this.contentElements.forEach((content) => {
                    content.style.width = `${this.contentWidth}px`;
                    content.style.flexShrink = "0";
                });

                // Disconnect observer as we no longer need it
                if (this.resizeObserver) {
                    this.resizeObserver.disconnect();
                    this.resizeObserver = null;
                }

                this.setupComplete = true;

                // If initial animation is enabled, run it first before starting marquee
                if (this.initialAnimationEnabled && !this.initialAnimationComplete) {
                    this.runInitialAnimation().catch((error) => {
                        console.error("Initial animation error:", error);
                        // Fallback: start marquee even if animation fails
                        this.startAnimation();
                    });
                } else {
                    this.startAnimation();
                }

                // Handle window resize
                this.resizeHandler = this.debounce(() => {
                    this.handleResize();
                }, 100);

                window.addEventListener("resize", this.resizeHandler);
            }
        };

        // Create ResizeObserver to watch for layout changes
        this.resizeObserver = new ResizeObserver((entries) => {
            checkAndSetup();
        });

        // Observe all content elements
        this.contentElements.forEach((content) => {
            this.resizeObserver.observe(content);
        });

        // Also check immediately in case elements are already laid out
        checkAndSetup();
    }

    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Disable page scrolling
     */
    disablePageScroll() {
        document.body.classList.add('initial-animation-active');
    }

    /**
     * Enable page scrolling
     */
    enablePageScroll() {
        document.body.classList.remove('initial-animation-active');
    }

    /**
     * Returns true when we should not block scroll during the intro (hash in URL or intro already seen).
     */
    shouldAllowScrollDuringIntro() {
        if (typeof location === "undefined") return false;
        if (location.hash && location.hash.length > 1) return true;
        try {
            return localStorage.getItem("rachtiangallery_intro_seen") === "1";
        } catch {
            return false;
        }
    }

    /**
     * Initial animation sequence using GSAP Timeline
     * This will be called before the marquee starts animating when initial animation is enabled
     * @returns {Promise} Resolves when initial animation is complete
     */
    runInitialAnimation() {
        return new Promise((resolve, reject) => {
            try {                
                // Check if GSAP is available
                if (typeof gsap === "undefined") {
                    console.warn("GSAP is not loaded. Initial animation will be skipped.");
                    this.initialAnimationComplete = true;
                    resolve();
                    return;
                }

                const section = this.marquee.closest(".marquee-hero");
                if (!section) {
                    this.initialAnimationComplete = true;
                    resolve();
                    return;
                }

                // Only block scroll and force top when we're not allowing scroll (no hash, first visit)
                if (!this.shouldAllowScrollDuringIntro()) {
                    this.disablePageScroll();
                    window.scrollTo(0, 0);
                }

                // Get all elements needed for animation
                const initialContent = section.querySelector("[data-initial-animation-content]");
                const initialLogo = section.querySelector("[data-initial-logo]");
                const initialTextLeft = section.querySelector(".marquee-hero__initial-text-left");
                const initialTextRight = section.querySelector(".marquee-hero__initial-text-right");
                const media = section.querySelector(".marquee-hero__media");
                const button = section.querySelector(".border-button, .solid-button"); // It will only be one or the other
                const header = document.getElementById("shopify-section-homepage-header");
                const cover = section.querySelector("[data-initial-cover]");

                if (!initialContent || !initialLogo || !initialTextLeft || !initialTextRight) {
                    this.initialAnimationComplete = true;
                    resolve();
                    return;
                }

                // Detect mobile (matching CSS breakpoint at 750px)
                const isMobile = window.innerWidth < 750;

                // Calculate distances for text separation and exit
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                // Desktop: horizontal animation (left/right)
                // Mobile: vertical animation (up/down)
                const leftExitDistance = isMobile ? -viewportHeight * 0.4 : -viewportWidth * 0.6;
                const rightExitDistance = isMobile ? viewportHeight * 0.4 : viewportWidth * 0.6;
                const separationDistance = isMobile ? 35 : 60; // Distance to move text apart for logo

                // Create GSAP timeline
                const tl = gsap.timeline({
                    onComplete: () => {
                        // Cleanup: remove initial content and cover, restore scroll
                        if (initialContent) {
                            initialContent.remove();
                        }
                        if (cover) {
                            cover.remove();
                        }
                        try {
                            localStorage.setItem("rachtiangallery_intro_seen", "1");
                        } catch (_) {}

                        this.initialAnimationComplete = true;
                        resolve();
                    },
                    onError: (error) => {
                        console.error("GSAP animation error:", error);
                        // Ensure scroll is restored even on error
                        this.enablePageScroll();
                        this.initialAnimationComplete = true;
                        reject(error);
                    },
                });

                // Set initial states
                gsap.set(initialContent, { opacity: 0 });
                gsap.set(initialLogo, { opacity: 0, visibility: "hidden" });
                // Mobile uses y (vertical), desktop uses x (horizontal)
                if (isMobile) {
                    gsap.set(initialTextLeft, { y: 0 });
                    gsap.set(initialTextRight, { y: 0 });
                } else {
                    gsap.set(initialTextLeft, { x: 0 });
                    gsap.set(initialTextRight, { x: 0 });
                }

                // Animation sequence
                // 1. Fade in initial content
                tl.to(initialContent, {
                    opacity: 1,
                    duration: 0.6,
                    ease: "power2.out",
                });

                // 2. Text moves apart
                // Mobile: vertical movement (up/down), Desktop: horizontal movement (left/right)
                if (isMobile) {
                    tl.to(
                        [initialTextLeft, initialTextRight],
                        {
                            y: (index) => (index === 0 ? -separationDistance : separationDistance),
                            duration: 0.8,
                            ease: "power2.inOut",
                        },
                        "+=0.5",
                    );
                } else {
                    tl.to(
                        [initialTextLeft, initialTextRight],
                        {
                            x: (index) => (index === 0 ? -separationDistance : separationDistance),
                            duration: 0.8,
                            ease: "power2.inOut",
                        },
                        "+=0.5",
                    );
                }

                // 3. Logo fades in
                tl.to(initialLogo, {
                    opacity: 1,
                    visibility: "visible",
                    duration: 0.6,
                    ease: "power2.out",
                });

                // 4. Text animates off-screen + content fades out
                // Mobile: vertical exit (up/down), Desktop: horizontal exit (left/right)
                if (isMobile) {
                    tl.to(
                        initialTextLeft,
                        {
                            y: leftExitDistance,
                            duration: 1,
                            ease: "power2.in",
                        },
                        "exit",
                    );

                    tl.to(
                        initialTextRight,
                        {
                            y: rightExitDistance,
                            duration: 1,
                            ease: "power2.in",
                        },
                        "exit",
                    );
                } else {
                    tl.to(
                        initialTextLeft,
                        {
                            x: leftExitDistance,
                            duration: 1,
                            ease: "power2.in",
                        },
                        "exit",
                    );

                    tl.to(
                        initialTextRight,
                        {
                            x: rightExitDistance,
                            duration: 1,
                            ease: "power2.in",
                        },
                        "exit",
                    );
                }

                tl.to(
                    initialContent,
                    {
                        opacity: 0,
                        duration: 1,
                        ease: "power2.in",
                    },
                    "exit",
                );

                // 5. Background media fades in
                if (media) {
                    tl.to(
                        media,
                        {
                            opacity: 1,
                            duration: 0.8,
                            ease: "power2.out",
                        },
                        "exit+=1",
                    );
                }

                // 6. Marquee and header fade in simultaneously, marquee animation starts
                tl.to(
                    this.marquee,
                    {
                        opacity: 1,
                        duration: 0.8,
                        ease: "power2.out",
                        onStart: () => {
                            // Start marquee animation at the same time as fade-in
                            this.startAnimation();
                            this.enablePageScroll();
                        },
                    },
                    "-=0.4",
                );

                // Header fades in at the same time as marquee
                if (header) {
                    tl.to(
                        header,
                        {
                            opacity: 1,
                            duration: 0.8,
                            ease: "power2.out",
                        },
                        "<",
                    );
                }

                // Brown cover fades out so page is visible
                if (cover) {
                    tl.to(
                        cover,
                        {
                            opacity: 0,
                            duration: 0.8,
                            ease: "power2.out",
                        },
                        "<",
                    );
                }

                // 7. Button fades in
                if (button) {
                    tl.to(button, {
                        opacity: 1,
                        duration: 0.6,
                        ease: "power2.out",
                    });
                }
            } catch (error) {
                console.error("Initial animation setup error:", error);
                this.initialAnimationComplete = true;
                reject(error);
            }
        });
    }

    handleResize() {
        const firstContent = this.contentElements[0];
        const newContentWidth = firstContent.scrollWidth || firstContent.offsetWidth;
        const containerWidth = this.marquee.offsetWidth;

        if (newContentWidth !== this.contentWidth && newContentWidth > 0) {
            this.contentWidth = newContentWidth;
            this.contentElements.forEach((content) => {
                content.style.width = `${this.contentWidth}px`;
            });
        }

        if (this.contentWidth <= containerWidth && this.isAnimating) {
            this.stopAnimation();
        } else if (this.initialAnimationComplete) {
            this.startAnimation();
        }
    }

    startAnimation() {
        // Only animate if content is wider than container and not already animating
        if (this.isAnimating || this.contentWidth <= this.marquee.offsetWidth) {
            return;
        }

        this.isAnimating = true;
        this.lastFrameTime = null;
        this.animate();
    }

    stopAnimation() {
        this.isAnimating = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        this.lastFrameTime = null;
    }

    animate() {
        if (!this.isAnimating) {
            return;
        }

        const now = performance.now();
        if (this.lastFrameTime === null) {
            this.lastFrameTime = now;
        }

        const deltaTime = (now - this.lastFrameTime) / 1000; // Convert to seconds
        this.lastFrameTime = now;

        // Move RTL (right to left) - negative direction
        this.currentPosition -= this.speed * deltaTime;

        // Reset position when first content block is completely off-screen
        // This creates seamless loop since second block is identical
        if (Math.abs(this.currentPosition) >= this.contentWidth) {
            this.currentPosition = 0;
        }

        // Apply transform with linear easing (no transition for smooth animation)
        this.track.style.transform = `translate3d(${this.currentPosition}px, 0, 0)`;
        this.track.style.willChange = "transform";

        this.animationId = requestAnimationFrame(() => this.animate());
    }

    destroy() {
        this.stopAnimation();
        if (this.resizeHandler) {
            window.removeEventListener("resize", this.resizeHandler);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }
}

// Store marquee instances for cleanup
const marqueeInstances = new WeakMap();

// Initialize marquee when DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMarquees);
} else {
    initMarquees();
}

function initMarquees() {
    const marquees = document.querySelectorAll("[data-marquee]");
    marquees.forEach((marquee) => {
        // Clean up existing instance if any
        const existingInstance = marqueeInstances.get(marquee);
        if (existingInstance) {
            existingInstance.destroy();
        }

        const instance = new RachtianHeroMarquee(marquee);
        marqueeInstances.set(marquee, instance);
    });
}

// Re-initialize when Shopify theme editor loads sections
if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", (event) => {
        const marquee = event.detail.querySelector("[data-marquee]");
        if (marquee) {
            const instance = new RachtianHeroMarquee(marquee);
            marqueeInstances.set(marquee, instance);
        }
    });

    document.addEventListener("shopify:section:unload", (event) => {
        const marquee = event.detail.querySelector("[data-marquee]");
        if (marquee) {
            const instance = marqueeInstances.get(marquee);
            if (instance) {
                instance.destroy();
                marqueeInstances.delete(marquee);
            }
        }
    });
}
